/* eslint-disable no-console */
import { log } from '@axa-fr-axion/log';
import { FunctionalErrorReason } from '@axa-fr-axion/restful';
import {
    Civilite,
    Conseiller,
    Dossier,
    Personne,
    Adresse,
    OrigineData,
    UserJourneyServicesConfig,
    Funnel,
} from '@axa-fr-axion/user-journey-services';
import { OptionData } from '@axafr-btc-ds/form-select';
import { mapAnalyticToProprietes } from '@axa-fr-axion/analytic-cookie';
import { produce } from 'immer';
import merge from 'lodash.merge';
import {
    AppOptions,
    JourneyData,
    JourneyDataValidity,
    JourneyInitialData,
} from './App.interface';
import {
    HomeAddressData,
    IdentityData,
} from 'features/contact';
import { GenderOptions } from 'features/contact/identity/gender';
import { Establishment, EstablishmentEffectiveSlice } from 'services/shared';
import { NavigationPath, StepNavigationPath } from 'interfaces';
import { PresetDefinition } from 'presets';
import { InsuranceDelayData } from 'features/insurance-delay';
import {
    DISABLE_DELAY,
    HEADERS_CORRELATION_NAME,
    BFF_HOST,
    BFF_PATHNAME,
    BFF_FOLDER_PATH,
    BFF_LEAD_PATH,
    BFF_PERSON_PATH,
    BFF_FIND_ADVISORS_ENDPOINT,
    BFF_IDENTIFY_ENDPOINT,
    BFF_REQUEST_CONTACT_ENDPOINT,
} from 'utils';
import { differenceInYears, parse } from 'date-fns';

export const findAgentLink = 'https://www.findagent.com';
export const DEFAULT_OPTIONS: AppOptions = {
    editorialCommon: {
        axaPartnerPhoneNumber: '0970821010',
        findAdvisorWhenApartFromMetropolisLink: findAgentLink,
        findAdvisorWhenUnreliableCoordinatesLink: findAgentLink,
        findAdvisorWhenRejectedLink: findAgentLink,
    },
};

export const EMPTY_JOURNEY_VALIDITY_DATA: JourneyDataValidity = {
    isIdentityValid: false,
    isHomeAddressValid: false,
    isLegalStatusValid: false,
    isIncomeValid: false,
    isSelectedAdvisorValid: false,
    isProprietesValid: false,
    isBirthDateValid: false,
    isInsuranceDelayValid: false,
    isVehicleIdentificationChoiceValid: false,
    isLicenceValid: false,
    isVehicleValid: false,
    isBonusPenaltyCoefficientValid: false,
    isScenariosValid: true,
    isPricingValid: true,
};

export const config: UserJourneyServicesConfig = {
    isDelayDisabled: DISABLE_DELAY,
    headersCorrelationName: HEADERS_CORRELATION_NAME,
    bffHost: BFF_HOST,
    bffPathName: BFF_PATHNAME,
    bffFolderPathName: BFF_FOLDER_PATH,
    bffLeadPathName: BFF_LEAD_PATH,
    bffPersonPathname: BFF_PERSON_PATH,
    bffFindAdvisorsEndpoint: BFF_FIND_ADVISORS_ENDPOINT,
    bffIdentifyEndpoint: BFF_IDENTIFY_ENDPOINT,
    bffRequestContactEndpoint: BFF_REQUEST_CONTACT_ENDPOINT,
};

export const EMPTY_JOURNEY_DATA: JourneyData = {
    ...EMPTY_JOURNEY_VALIDITY_DATA,
    legalStatus: undefined,
    income: undefined,
    folder: {
        id: undefined,
        lead: {
            id: undefined,
        },
    },
    identity: {
        gender: undefined,
        firstname: undefined,
        lastname: undefined,
        phone: undefined,
        email: undefined,
    },
    homeAddress: {
        address: undefined,
        addressComplement: undefined,
        postalCode: undefined,
        city: undefined,
    },
    proprietes: undefined,
    birthDate: undefined,
    insuranceDelay: undefined,
    vehicleIdentificationChoice: undefined,
    vehicle: {
        brand: undefined,
        model: undefined,
        licensePlate: '',
        circulationDate: {
            year: undefined,
            month: undefined,
        },
        bodyType: undefined,
        motorizationType: undefined,
        allInformations: undefined,
    },
    bonusPenaltyCoefficient: undefined,
    scenarios: undefined,
    selectedAdvisor: undefined,
};

export const ORIGIN_ITEM_KEY_FOR_PRODUCT = 'produits';

export const initJourneyDataWithOrigin = (
    origin?: OrigineData,
) => (
    initialData: Partial<JourneyInitialData> | undefined,
): JourneyData => {
    let initialDataFromOrigin: Partial<JourneyInitialData> = {};

    const preSelectedProductsFromOrigin = origin?.items?.find(item => item.nom === ORIGIN_ITEM_KEY_FOR_PRODUCT);
    if (!!preSelectedProductsFromOrigin?.valeur) {
        initialDataFromOrigin = {
        };
    }

    return merge(
        {},
        EMPTY_JOURNEY_DATA,
        initialData,
        initialDataFromOrigin,
    );
};

export function getInitialStep(
    options: AppOptions,
    preset: PresetDefinition
): StepNavigationPath {
    return 'VehicleIdentificationChoice';
}

export const folderReady = (journeyData: JourneyData): boolean =>
    Boolean(journeyData.folder.id && journeyData.folder.lead?.id);

export const assignProprietes = (journeyData: JourneyData): Dossier => produce(journeyData.folder, (draft) => {
    draft.proprietes = [ ...(journeyData.folder.proprietes ?? []) ];

    mapInsuranceDelayToProprietes(draft, journeyData.insuranceDelay);
    return draft;
});

export const assignProprietesForMicroEnt = (journeyData: JourneyData): Dossier => produce(
    journeyData.folder,
    (draft) => {
        const journeyProperties = Object.assign([], journeyData.proprietes);
        const origineItems = Object.assign([], journeyData.folder.proprietes);
        draft.proprietes = [ ...origineItems, ...journeyProperties ];
        return draft;
    }
);

export const assignProductCodesAndAnalyticsProprietes =
    (journeyData: JourneyData): Dossier => produce(journeyData.folder, (draft) => {
        draft.proprietes = [ ...(journeyData.folder.proprietes ?? []) ];
        mapAnalyticToProprietes(draft, journeyData.analytic);
    });

export const toCivilite = (gender?: GenderOptions): Civilite | undefined => {
    if (!gender) {
        return undefined;
    }
    switch (gender) {
        case 'ms':
            return 'Mme';
        case 'mr':
            return 'M';
        default:
            throw new Error(`Unknown gender [${ gender }]`);
    }
};

export const toGender = (civilite?: Civilite | string | null): GenderOptions | undefined => {
    switch (civilite) {
        case 'M':
            return 'mr';
        case 'Mme':
            return 'ms';
        default:
            return undefined;
    }
};

export const toAdvisor = (advisor: Conseiller) => {
    const city = [
        advisor.adresse?.codePostal,
        advisor.adresse?.ville,
    ].filter(Boolean).join(' ');
    const streets = [
        advisor.adresse?.adresse,
        advisor.adresse?.[ 'complement-adresse' ],
    ].filter(Boolean) as Array<string>;
    return {
        avatar: advisor.urlPhoto,
        firstName: advisor.prenom,
        lastName: advisor.nom,
        status: advisor.reseau,
        streets,
        city: city || undefined,
        phone: advisor.tel1 || advisor.tel2,
        mail: advisor.email,
        openingHours: advisor.openingHours?.at(0),
        website: advisor.url,
        orias: advisor.orias,
        labelCommercial: advisor.libelleCommercial,
    };
};

export const toPersonne = (identity: IdentityData, address: HomeAddressData): Personne => ({
    nom: identity.lastname,
    prenom: identity.firstname,
    civilite: toCivilite(identity.gender),
    adresse: toAdresse(address),
    telephone: identity.phone,
    email: identity.email?.trim(),
    optinAxa: identity.isOptin,
});

export const toIdentity = (personne: Personne | undefined): IdentityData => ({
    lastname: personne?.nom,
    firstname: personne?.prenom,
    gender: toGender(personne?.civilite),
    phone: personne?.telephone,
    email: personne?.email,
    isOptin: personne?.optinAxa,
});

export const toHomeAdress = (adresse: Adresse | undefined): HomeAddressData => ({
    address: adresse?.adresse,
    city: adresse?.ville,
    postalCode: adresse?.codePostal,
});

const endSteps: Record<Funnel | FunctionalErrorReason | string, StepNavigationPath> = {
    'PERSONALISATION': 'End/Personalisation',
    'AXAPART': 'End/AxaPartner',
    'SOLLICITATION': 'End/Sollicitation',
    'FIABILITE': 'End/Fiabilite',
    'HORS-METROPOLE': 'End/HorsMetropole',
    'HORS-LIMITE': 'End/HorsLimite',
    'REJET-SEC': 'End/RejetSec',
    'HORS-CIBLE': 'End/HorsCible',
    'DETENTEUR': 'End/Detenteur',
    'PRE-DETENTEUR': 'End/Detenteur',
    'WEBCALLBACK': 'End/Webcallback',
    'ABORT': 'End/Abort',
    'CSI': 'End/CSI',
    'HORS-CIBLE-PARCOURS': 'End/HorsCibleParcours',
    'Consentement': 'End/Consentement',
    'HORS-CIBLE-PARCOURS-CSI': 'End/HorsCibleParcoursCSI',
};

export const chooseEndStep = (inCase?: Funnel | FunctionalErrorReason | string): StepNavigationPath => {
    const step = inCase ? endSteps[ inCase ] : '';
    if (!step) {
        log.error(`Unknown case ${ inCase }`);
        return 'Error';
    }
    return step;
};

export const fromEstablishment = (establishment: Establishment | undefined): HomeAddressData | undefined => {
    if (!establishment) {
        return undefined;
    }

    const { address } = establishment;
    const { city, row4, zip_code } = address;

    if (!zip_code || !city) {
        return undefined;
    }

    return {
        address: row4,
        city,
        postalCode: zip_code,
    };
};

export function mapInsuranceDelayToProprietes(folder: Dossier, data: InsuranceDelayData | undefined): Dossier {
    if (data && Boolean(folder.proprietes) && Array.isArray(folder.proprietes)) {
        folder.proprietes.push({
            nom: 'delaiAssurance',
            valeur: data,
        });
    }

    return folder;
}

export function getValue(data: string | OptionData): string {
    if (typeof data === 'string') {
        return data;
    }

    return data.label;
}

export function getNumberOfEmployeesFromEstablishment(
    establishment: Establishment | undefined
): Exclude<EstablishmentEffectiveSlice, 'NN'> | undefined {
    const value = establishment?.establishment_effective_slice;

    if (!value) {
        return undefined;
    }

    if (value === 'NN') {
        return '00';
    }

    return value;
}

export function mapHomeAddressDataToProprietes(folder: Dossier, data: HomeAddressData): Dossier {
    if (folder.proprietes) {
        folder.proprietes = [
            ...folder.proprietes,
            {
                nom: 'raison',
                valeur: '',
            },
        ];
    }

    return folder;
}

export const toAdresse = (address: HomeAddressData): Adresse => ({
    adresse: address.address,
    codePostal: address.postalCode,
    ville: address.city,
    pays: 'FRA',
});

const hasManyAdvisors = (folder: Dossier): boolean => (folder.lead?.conseillers && folder.lead?.conseillers.length > 1)
    || false;
export const selectDefaultAdvisor = (folder: Dossier): Conseiller | undefined => {
    if (hasManyAdvisors(folder)) {
        return undefined;
    }
    return folder.lead?.conseillers?.at(0);
};

export const choosePostContactStep = (folder: Dossier, hasPricing = false): NavigationPath => {
    if (hasManyAdvisors(folder)) {
        return 'ChooseAdvisor';
    }

    if (hasPricing) {
        return 'PreTarification';
    }

    return 'PreConfirmation';
};

export const putAdvisorInLead = (folder: Dossier, advisor?: Conseiller): Dossier => ({
    ...folder,
    lead: {
        ...folder.lead,
        conseillers: advisor ? [ advisor ] : folder.lead?.conseillers,
    },
});

export function isCatalogVisibleFromOrigin(data?: OrigineData): boolean {
    return !data?.items?.find(item => item.nom === ORIGIN_ITEM_KEY_FOR_PRODUCT && !!item.valeur?.length);
}

export const capitalizeFirstLetter = (input: string): string => {
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
};

export const resizeWindow = (x: number, y: number) => {
    window.innerWidth = x;
    window.innerHeight = y;
    window.dispatchEvent(new Event('resize'));
};

export const calculateAge = (birthdate: string): number => {
    const date = parse(birthdate, 'yyyy-MM-dd', new Date());
    return differenceInYears(new Date(), date);
};

export const getValueOrDefault = <T>(value: T, defaultValue: Exclude<T, null | undefined>) => {
    return value ?? defaultValue;
};

export const isIdentityValid = (identity: IdentityData) => {
    return Boolean(identity.gender && identity.firstname && identity.lastname
    && identity.phone && identity.email);
};
